import { memo, ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import { Checkbox, Form, Modal, ModalProps } from 'antd';

import FormField from 'components/FormField';

const StyledForm = styled(Form)`
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CheckboxGroup = styled(Checkbox.Group)`
  margin-top: 5px;
  > label {
    margin-right: 8px;
  }
  > label + label {
    margin-left: 0 !important;
  }
`;

export type PriorityFormValues = Assistant.Priority;

type FV = PriorityFormValues;

export type PriorityFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface PriorityFormProps extends FormikConfig<FV> {
  children?: ReactNode | FormRenderChildren<FV>;
}

function PriorityForm(props: PriorityFormProps) {
  const { initialValues, children, ...otherProps } = props;

  const isRenderProps = typeof children === 'function';

  const { t } = useTranslate();

  return (
    <Formik enableReinitialize validateOnMount initialValues={initialValues} {...otherProps}>
      {(props) => {
        const form = (
          <StyledForm>
            <FormField<FV['tradeStatusList']> name="tradeStatusList" label={t('field.tradeStatus')}>
              {({ field: { value, onChange } }) => {
                return (
                  <CheckboxGroup
                    value={value}
                    onChange={(value) => onChange(value as Assistant.TradeStatus[])}
                  >
                    {['NO_TRADE', 'NO_PAY', 'WAIT_SHIP', 'SHIPPING', 'RECENT_TRADED', 'REFUND'].map(
                      (tradeStatus) => {
                        return (
                          <Checkbox key={tradeStatus} value={tradeStatus}>
                            {t(`tradeStatus.${tradeStatus}`)}
                          </Checkbox>
                        );
                      },
                    )}
                  </CheckboxGroup>
                );
              }}
            </FormField>
            <FormField<FV['buyerTagList']> name="buyerTagList" label={t('field.buyerTag')}>
              {({ field: { value, onChange } }) => {
                return (
                  <Checkbox.Group
                    value={value}
                    onChange={(value) => onChange(value as Assistant.BuyerTag[])}
                  >
                    {['NEW', 'OLD'].map((buyerTag) => {
                      return (
                        <Checkbox key={buyerTag} value={buyerTag}>
                          {t(`buyerTag.${buyerTag}`)}
                        </Checkbox>
                      );
                    })}
                  </Checkbox.Group>
                );
              }}
            </FormField>
          </StyledForm>
        );

        return isRenderProps ? (children as FormRenderChildren<FV>)(form, props) : form;
      }}
    </Formik>
  );
}

export interface PriorityFormModalProps extends ModalProps, Pick<PriorityFormProps, 'onSubmit'> {
  initialValues?: PriorityFormProps['initialValues'];
  onCancel: Required<ModalProps>['onCancel'];
}

function FormModal(props: PriorityFormModalProps) {
  const { initialValues, onSubmit, onCancel, ...otherProps } = props;

  const { t } = useTranslate();

  if (initialValues === undefined) {
    return null;
  }

  return (
    <PriorityForm initialValues={initialValues} onSubmit={onSubmit}>
      {(form, props) => {
        const { isValid, dirty, isSubmitting, handleSubmit, resetForm } = props;

        return (
          <Modal
            width={460}
            title={t('group.priority')}
            okButtonProps={{
              children: t('group.save'),
              disabled: !isValid,
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            onCancel={(e) => {
              if (dirty) {
                Modal.confirm({
                  title: t('group.priorityCancelConfirm'),
                  onOk: (e) => {
                    onCancel(e);
                    resetForm();
                  },
                });
              } else {
                onCancel(e);
              }
            }}
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </PriorityForm>
  );
}

export const PriorityFormModal = memo(FormModal);

export default memo(PriorityForm);
